import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, map, Observable, tap } from 'rxjs';

import { PortalLeadService } from '@app/services/portal-lead/portal-lead.service';
import { SessionProceedCoordinator } from '@app/services/session-proceed-coordinator.service';
import { buildAncillaryBundleSignInRedirectExtras } from '@app/utils/portal-sign-in-extras.util';

import {
  ancillaryDescriptions,
  ancillaryImg,
  ancillaryNonIncludedDescription,
  ancillaryTitles,
  generateAncillaryNonIncludedTitle,
  nonIncludedAncillaryImg,
} from '@app/pages/ancillary-bundle-page/ancillary-bundle.constants';
import {
  AncillaryAllBenefits,
  AncillaryBundleItem,
  AncillaryStatusData,
} from '@app/pages/ancillary-bundle-page/ancillary-bundle.interfaces';
import {
  AncillaryTypeEnum,
  PortalAncillaryBundle,
  PortalOrderNode,
} from '@app/services/api/api.types';
import { getFormatedCurrency } from '@app/utils/utils';
import { PortalRoutes } from '@app/app-routing.module';
import { PortalOrderService } from '@app/services/portal-order/portal-order.service';
import { StateService } from '@app/services/state/state.service';
import { Steps } from '@app/components/stepper/stepper.component';
import { PortalTrackingEvents } from '@app/utils/tracking-events';
import { Tracking } from '@app/services/tracking/tracking.service';
import { PopupsService } from '@voyagu/portal-design-system';
import { AncillaryBundleItemType } from '@app/pages/ancillary-bundle-page/ancillary-bundle.enum';

@Component({
  selector: 'ancillary-bundle-page',
  templateUrl: './ancillary-bundle-page.component.html',
  styleUrls: ['./ancillary-bundle-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [SessionProceedCoordinator],
})
export class AncillaryBundlePageComponent implements OnInit {
  Steps = Steps;

  changeDetectorRef = inject(ChangeDetectorRef);
  location = inject(Location);
  router = inject(Router);
  portalOrderService = inject(PortalOrderService);
  portalLeadService = inject(PortalLeadService);
  stateService = inject(StateService);
  popupsService = inject(PopupsService);
  activatedRoute = inject(ActivatedRoute);
  sessionProceed = inject(SessionProceedCoordinator);

  portalOrder$: Observable<PortalOrderNode | null>;

  portalOrder: PortalOrderNode | null;

  selectedAncillary: AncillaryBundleItem | null = null;

  isBundleSelected: boolean;
  isBundleAvailable: boolean;
  isCancelForAnyReasonAvailable: boolean;
  isDisruptionProtectionAvailable: boolean;
  isBundleSelectActions: boolean;

  selectedAllBenefits: AncillaryAllBenefits | null = null;
  ancillariesItems: AncillaryBundleItem[] = [];
  ancillaryStatuses: AncillaryStatusData[] = [];
  ancillaryFeatures: Record<string, { title: string; description: string }[]> = {};

  disruptionProtection?: number;
  cancelForAnyReason?: number;
  bundle?: PortalAncillaryBundle;

  discountSize = 0;

  ancillaryUpsell: boolean;

  withPayAndConfirm: boolean;

  isNewUser = false;

  @ViewChild('allBenefitsTemplate') allBenefitsTemplate: TemplateRef<unknown>;
  @ViewChild('askToSignInTemplate') askToSignInTemplate: TemplateRef<unknown>;

  ngOnInit() {
    this.ancillaryUpsell = this.activatedRoute.snapshot.data['ancillaryUpsell'] as boolean;

    Tracking.track(PortalTrackingEvents.portalAncillaryBundleOpened);

    this.portalOrder$ = combineLatest([
      this.portalOrderService.portalOrder$,
      this.portalLeadService.portalLead$,
    ]).pipe(
      tap(([portalOrder, portalLead]) => {
        this.isNewUser = !!portalLead?.isNewUser;

        this.portalOrder = portalOrder;

        this.withPayAndConfirm = !portalOrder?.isDemo;

        if (portalOrder) {
          const cancelForAnyReason = portalOrder.ancillaries.find(
            (ancillary) => ancillary.type === AncillaryTypeEnum.CANCEL_FOR_ANY_REASON,
          );

          const disruptionProtection = portalOrder.ancillaries.find(
            (ancillary) => ancillary.type === AncillaryTypeEnum.DISRUPTION_PROTECTION,
          );

          this.ancillaryFeatures = {
            [AncillaryBundleItemType.CancelForAnyReason]: [
              {
                title: 'Flexible cancellations',
                description:
                  'Cancel your trip for any reason up to 48 hours before departure and receive a refund.',
              },
              {
                title: 'Generous refund options',
                description: `Receive 80% <b>(${getFormatedCurrency(cancelForAnyReason?.refundPerTraveler ?? 0)}/per traveler)</b> cash refund or 100% <b>(${getFormatedCurrency(cancelForAnyReason?.balanceRefundPerTraveler ?? 0)}/per traveler)</b> as a travel credit.`,
              },
              {
                title: 'No cancellation fees',
                description: 'The cancellation fee is waived, making the process hassle-free.',
              },
            ],
            [AncillaryBundleItemType.PremiumDisruptionAssistance]: [
              {
                title: 'Alternative flight flexibility',
                description:
                  "We'll find and cover rebooking costs up to the original ticket price.",
              },
              {
                title: 'Instant refunds',
                description:
                  'Receive an instant refund in travel credit or cash within 7–10 business days.',
              },
              {
                title: 'End-to-end itinerary assistance',
                description:
                  "We'll rearrange your entire itinerary for trips with multiple bookings to ensure minimal disruption.",
              },
              {
                title: 'Rebooking coverage',
                description:
                  'We cover rebooking costs up to the original ticket price plus an additional $1,000 to handle disruptions smoothly.',
              },
              {
                title: '24/7 support',
                description: 'Get round-the-clock help for any travel disruptions.',
              },
            ],
          };

          let includeNone = false;

          if (disruptionProtection) {
            this.isDisruptionProtectionAvailable = true;

            const ancillary = {
              price: +(disruptionProtection.price ?? 0),
              pricePerTraveler: disruptionProtection.amountPerTraveler,
              type: AncillaryBundleItemType.PremiumDisruptionAssistance,
              prefix: this.portalOrder?.isChange ? 'Keep for ' : '',
            };

            this.ancillariesItems.push(ancillary);

            includeNone = includeNone || Boolean(ancillary.price);
          }

          if (cancelForAnyReason) {
            this.isCancelForAnyReasonAvailable = true;

            const ancillary = {
              price: +(cancelForAnyReason.price ?? 0),
              pricePerTraveler: cancelForAnyReason.amountPerTraveler,
              type: AncillaryBundleItemType.CancelForAnyReason,
              prefix: this.portalOrder?.isChange ? 'Keep for ' : '',
            };

            this.ancillariesItems.push(ancillary);

            includeNone = includeNone || Boolean(ancillary.price);
          }

          const bundle = this.portalOrder?.ancillaryBundle;

          if (bundle) {
            this.isBundleAvailable = true;

            const bundleAncillary = {
              price: bundle.price,
              pricePerTraveler: bundle.pricePerTraveler,
              priceWithoutDiscount: bundle.priceWithoutDiscount,
              priceWithoutDiscountPerTraveler: bundle.priceWithoutDiscountPerTraveler,
              type: AncillaryBundleItemType.Bundle,
              prefix: this.portalOrder?.isChange ? 'Keep for ' : '',
            };

            this.discountSize = bundle.discount * 100;
            this.ancillariesItems.push(bundleAncillary);

            includeNone = includeNone || Boolean(bundleAncillary.price);
          }

          if (!this.ancillaryUpsell && includeNone) {
            this.ancillariesItems.push({
              price: 0,
              pricePerTraveler: 0,
              type: AncillaryBundleItemType.None,
              isChange: this.portalOrder?.isChange,
            });
          }

          this.selectInitialAncillary();
        }
      }),
      map(([portalOrder]) => portalOrder),
    );
  }

  get isSeparatedStatusesCards(): boolean {
    return (
      this.selectedAncillary?.type === AncillaryBundleItemType.CancelForAnyReason ||
      this.selectedAncillary?.type === AncillaryBundleItemType.PremiumDisruptionAssistance
    );
  }

  selectInitialAncillary() {
    const state = this.stateService.getState();

    if (state.bundleRestorePending && this.portalOrder?.ancillaryBundle) {
      const bundleAncillary = this.ancillariesItems.find(
        (item) => item.type === AncillaryBundleItemType.Bundle,
      );

      this.stateService.patchState({ bundleRestorePending: undefined });

      if (bundleAncillary) {
        this.selectAncillary(bundleAncillary);

        return;
      }
    }

    if (state.disruptionProtection || state.disruptionProtection === 0) {
      const ancillary = this.ancillariesItems.find(
        (item) => item.type === AncillaryBundleItemType.PremiumDisruptionAssistance,
      );

      if (ancillary) {
        this.selectAncillary(ancillary);
      }
    }

    if (state.cancelForAnyReason || state.cancelForAnyReason === 0) {
      const ancillary = this.ancillariesItems.find(
        (item) => item.type === AncillaryBundleItemType.CancelForAnyReason,
      );

      if (ancillary) {
        this.selectAncillary(ancillary);
      }
    }

    if (!this.selectedAncillary && this.portalOrder?.ancillaryBundle) {
      const ancillary = this.ancillariesItems.find(
        (item) => item.type === AncillaryBundleItemType.Bundle,
      );

      if (ancillary) {
        this.selectAncillary(ancillary);
      }
    }

    if (!this.selectedAncillary) {
      this.selectAncillary(this.ancillariesItems[0]);
    }
  }

  selectAncillary(ancillary: AncillaryBundleItem) {
    this.selectedAncillary = ancillary;
    this.updateSummary(ancillary);

    this.isBundleSelected = ancillary.type === AncillaryBundleItemType.Bundle;

    const ancillaryStatuses = [];

    if (this.isDisruptionProtectionAvailable) {
      ancillaryStatuses.push(
        this.generateAncillaryStatus(
          AncillaryBundleItemType.PremiumDisruptionAssistance,
          ancillary.type === AncillaryBundleItemType.PremiumDisruptionAssistance ||
            this.isBundleSelected,
        ),
      );
    }

    if (this.isCancelForAnyReasonAvailable) {
      ancillaryStatuses.push(
        this.generateAncillaryStatus(
          AncillaryBundleItemType.CancelForAnyReason,
          ancillary.type === AncillaryBundleItemType.CancelForAnyReason || this.isBundleSelected,
        ),
      );
    }

    ancillaryStatuses.sort((a, b) => {
      return (b.isIncluded ? 1 : 0) - (a.isIncluded ? 1 : 0);
    });

    this.ancillaryStatuses = ancillaryStatuses;
  }

  onSelectAncillary(ancillary: AncillaryBundleItem) {
    this.selectAncillary(ancillary);

    Tracking.track(
      PortalTrackingEvents.portalAncillaryBundleAncillaryPressed.replace(
        '{ancillary}',
        ancillary.type.toLowerCase(),
      ),
    );
  }

  updateSummary(ancillary: AncillaryBundleItem) {
    this.disruptionProtection = undefined;
    this.cancelForAnyReason = undefined;
    this.bundle = undefined;

    if (ancillary.type === AncillaryBundleItemType.CancelForAnyReason) {
      this.cancelForAnyReason = ancillary.price;
    }

    if (ancillary.type === AncillaryBundleItemType.PremiumDisruptionAssistance) {
      this.disruptionProtection = ancillary.price;
    }

    if (ancillary.type === AncillaryBundleItemType.Bundle) {
      this.bundle = this.portalOrder?.ancillaryBundle;
    }
  }

  generateAncillaryStatus(type: AncillaryBundleItemType, isIncluded: boolean): AncillaryStatusData {
    return {
      isIncluded,
      type,
      title: ancillaryTitles[type] ?? '',
      description: ancillaryDescriptions[type] ?? '',
      features: (this.ancillaryFeatures[type] ?? []).map((item) => item.title),
      img: isIncluded ? ancillaryImg[type] : nonIncludedAncillaryImg[type],
    };
  }

  generateAncillaryAllBenefits(data: AncillaryStatusData): AncillaryAllBenefits {
    const type = data.type;

    return {
      isWarning: false,
      title: ancillaryTitles[type],
      description: ancillaryDescriptions[type],
      features: this.ancillaryFeatures[type] ?? [],
      img: ancillaryImg[type],
    };
  }

  generateAncillaryWarning(data: AncillaryStatusData): AncillaryAllBenefits {
    const type =
      this.selectedAncillary?.type === AncillaryBundleItemType.None
        ? this.selectedAncillary.type
        : data.type;

    return {
      isWarning: true,
      title: generateAncillaryNonIncludedTitle(type) ?? '',
      description: ancillaryNonIncludedDescription[type],
      features: this.ancillaryFeatures[type] ?? [],
      img: nonIncludedAncillaryImg[type],
    };
  }

  onShowAllBenefits(ancillary: AncillaryStatusData) {
    this.selectedAllBenefits = this.generateAncillaryAllBenefits(ancillary);
    this.isBundleSelectActions = false;

    this.popupsService.showModal(this.allBenefitsTemplate);
  }

  showWarningModal(ancillary: AncillaryStatusData) {
    this.selectedAllBenefits = this.generateAncillaryWarning(ancillary);
    this.isBundleSelectActions = true;

    this.popupsService.showModal(this.allBenefitsTemplate);
  }

  closeModal() {
    this.selectedAllBenefits = null;
    this.popupsService.hideLast();
  }

  trackByAncillaryStatus(ancillaryStatuses: AncillaryStatusData): string {
    return ancillaryStatuses.title + ancillaryStatuses.isIncluded;
  }

  selectBundle() {
    const bundle = this.ancillariesItems.find(
      (item) => item.type === AncillaryBundleItemType.Bundle,
    );

    if (bundle) {
      this.selectAncillary(bundle);

      Tracking.track(
        PortalTrackingEvents.portalAncillaryBundleAncillaryPressed.replace(
          '{ancillary}',
          bundle.type.toLowerCase(),
        ),
      );

      this.changeDetectorRef.detectChanges();
    }
  }

  continueWithBundle() {
    this.runAfterSessionCheck(() => {
      this.selectBundle();

      this.submit();
    });
  }

  back() {
    this.location.back();

    Tracking.track(PortalTrackingEvents.portalAncillaryBundleBackPressed);
  }

  onContinueClick() {
    if (!this.isBundleSelected && this.isBundleAvailable) {
      const ancillaryStatus = this.ancillaryStatuses.find(
        (item) => item.type !== this.selectedAncillary?.type,
      );

      if (ancillaryStatus) {
        this.showWarningModal(ancillaryStatus);
        return;
      }
    }

    this.runAfterSessionCheck(() => this.submit());
  }

  runAfterSessionCheck(action: () => void) {
    const gateSignIn = this.getSubmitNextSegment() === PortalRoutes.Travelers;

    this.sessionProceed.runAfterSessionCheckIf(gateSignIn, this.isNewUser, action, () =>
      this.showAskToSignInModal(),
    );
  }

  showAskToSignInModal() {
    this.popupsService.showModal(this.askToSignInTemplate);
    this.sessionProceed.trackSignInFromCheckoutPromptOpened();
  }

  hideAskToSignInModal() {
    this.sessionProceed.dismissAskModal(() => this.popupsService.hideLast());
  }

  continueAsGuestFromSignInPrompt() {
    this.sessionProceed.continueAsGuestAfterPrompt(() => this.popupsService.hideLast());
  }

  showSignInModal() {
    const next = this.getSubmitNextSegment();
    const extras = buildAncillaryBundleSignInRedirectExtras(this.stateService.getState(), {
      bundleCardSelected: this.selectedAncillary?.type === AncillaryBundleItemType.Bundle,
      disruptionProtection: this.disruptionProtection,
      cancelForAnyReason: this.cancelForAnyReason,
      bundle: this.bundle,
    });

    if (this.ancillaryUpsell) {
      this.sessionProceed.openSignInModalForPortalOption(
        this.stateService.getState().portalLink,
        this.portalOrder?.portalLink ?? '',
        {
          ...extras,
          checkoutResume: next,
          checkoutPath: 'upsell',
          upsellTripPortalLink:
            this.activatedRoute.snapshot.paramMap.get(PortalRoutes.AncillaryUpsellParam) ??
            undefined,
        },
      );

      return;
    }

    this.sessionProceed.openSignInModalForPortalOption(
      this.stateService.getState().portalLink,
      this.portalOrder?.portalLink ?? '',
      {
        ...extras,
        checkoutResume: next,
        checkoutPath: 'option',
      },
    );
  }

  onModalContinueWithoutBundle() {
    this.closeModal();
    this.runAfterSessionCheck(() => this.submit());
  }

  onModalContinueWithBundle() {
    this.closeModal();
    this.runAfterSessionCheck(() => {
      this.selectBundle();
      this.submit();
    });
  }

  getSubmitNextSegment() {
    const afterBundleSkipsTravelers = this.ancillaryUpsell && this.withPayAndConfirm;

    if (this.portalOrder?.isChange) {
      return PortalRoutes.Gratuity;
    }

    if (afterBundleSkipsTravelers) {
      // The upsell journey never offers gratuity (TIC-7119).
      return PortalRoutes.OverviewPayment;
    }

    return PortalRoutes.Travelers;
  }

  submit() {
    if (!this.portalOrder) {
      return;
    }

    this.stateService.patchState({
      cancelForAnyReason: this.cancelForAnyReason,
      disruptionProtection: this.disruptionProtection,
      bundle: this.bundle,
    });

    const nextSegment = this.getSubmitNextSegment();

    const path = this.ancillaryUpsell
      ? [
          PortalRoutes.AncillaryUpsell,
          this.activatedRoute.snapshot.paramMap.get(PortalRoutes.AncillaryUpsellParam),
          nextSegment,
        ]
      : [this.stateService.getState().portalLink, this.portalOrder.portalLink, nextSegment];

    void this.router.navigate(path, { queryParamsHandling: 'merge' });

    Tracking.track(PortalTrackingEvents.portalAncillaryBundleContinuePressed);
  }
}
